import React, { useMemo } from 'react';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import DateRange from '@mui/icons-material/DateRange';
import NumbersOutlined from '@mui/icons-material/NumbersOutlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import ReceiptLong from '@mui/icons-material/ReceiptLong';
import Search from '@mui/icons-material/Search';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { logButtonClick } from 'utils/analytics';
import { useLogs } from '../hooks';
import {
  selectActivityLogsAllItems,
  selectActivityLogsCashIn,
  selectActivityLogsCashOut,
  selectActivityLogsLastSelectedScopeId,
  selectActivityLogsOnlyLogIds,
  selectActivityLogsStartLogId,
  selectActivityLogsEndLogId,
  selectActivityLogsUseTimeRange,
  selectActivityLogsViewAs,
  selectAssociateElevatedActivityLogs,
  selectComplianceActivityStaffId,
  selectIsComplianceActivity,
  selectLogSearch,
  selectLogSearchOptions,
  selectLogSearches,
  selectActivityLogsCombinedChecked,
  selectLogs,
  selectLogsValueMode,
  selectSingleLogTime,
  setActivityLogsSearchStatus,
} from '../redux';
import {
  buildActivityLogsSearchSummary,
  latestLogsButtonLabel,
  type ActivityLogsSearchSummaryIcon,
} from '../activityLogsSearchPayload';
import ActivityLogsSearchControls from './ActivityLogsSearchControls';
import { ModalParts } from 'pages/redux';
import * as S from './ActivitySearch.styled';

type Props = {
  setModal: (modal: ModalParts | null) => void,
  onSearch: () => void
  onLatest: () => void
};

const SUMMARY_ICONS: Record<ActivityLogsSearchSummaryIcon, React.ReactNode> = {
  scopes: <AccountTreeOutlined sx={{ fontSize: 18 }} />,
  time: <DateRange sx={{ fontSize: 18 }} />,
  value: <PaymentsOutlined sx={{ fontSize: 18 }} />,
  units: <NumbersOutlined sx={{ fontSize: 18 }} />,
  search: <Search sx={{ fontSize: 18 }} />,
  report: <ReceiptLong sx={{ fontSize: 18 }} />,
};

/** Default /logs right pane — search from the left-pane criteria. */
const ActivitySearch = ({ setModal, onSearch, onLatest }: Props) => {
  const dispatch = useAppDispatch();
  const { activityGroupsModel } = useLogs();
  const logs = useAppSelector(selectLogs);
  const logSearches = useAppSelector(selectLogSearches);
  const logSearch = useAppSelector(selectLogSearch);
  const logSearchOptions = useAppSelector(selectLogSearchOptions);
  const checked = useAppSelector(selectActivityLogsCombinedChecked);
  const valueMode = useAppSelector(selectLogsValueMode);
  const allItems = useAppSelector(selectActivityLogsAllItems);
  const cashIn = useAppSelector(selectActivityLogsCashIn);
  const cashOut = useAppSelector(selectActivityLogsCashOut);
  const useTimeRange = useAppSelector(selectActivityLogsUseTimeRange);
  const singleLogTime = useAppSelector(selectSingleLogTime);
  const onlyLogIds = useAppSelector(selectActivityLogsOnlyLogIds);
  const startLogId = useAppSelector(selectActivityLogsStartLogId);
  const endLogId = useAppSelector(selectActivityLogsEndLogId);
  const viewAs = useAppSelector(selectActivityLogsViewAs);
  const associateElevatedActivityLogs = useAppSelector(selectAssociateElevatedActivityLogs);
  const isComplianceActivity = useAppSelector(selectIsComplianceActivity);
  const complianceActivityStaffId = useAppSelector(selectComplianceActivityStaffId);
  const lastSelectedScopeId = useAppSelector(selectActivityLogsLastSelectedScopeId);

  const canExitToResults = Boolean(logs?.length);

  const exitToResults = () => {
    logButtonClick('activityLogs_exit_to_logs');
    dispatch(setActivityLogsSearchStatus('results'));
  };

  const summary = useMemo(() => buildActivityLogsSearchSummary({
    logSearches: logSearches || [],
    logSearch: logSearch || '',
    logSearchOptions,
    checked: checked || [],
    valueMode,
    allItems,
    cashIn,
    cashOut,
    useTimeRange: Boolean(useTimeRange && singleLogTime?.startDate && singleLogTime?.endDate),
    startDate: singleLogTime?.startDate,
    endDate: singleLogTime?.endDate,
    onlyLogIds,
    startLogId,
    endLogId,
    viewAs,
    associateElevatedActivityLogs: Boolean(associateElevatedActivityLogs),
    isComplianceActivity: Boolean(isComplianceActivity),
    complianceActivityStaffId,
    lastSelectedScopeId,
  }, activityGroupsModel), [
    logSearches,
    logSearch,
    logSearchOptions,
    checked,
    valueMode,
    allItems,
    cashIn,
    cashOut,
    useTimeRange,
    singleLogTime,
    onlyLogIds,
    startLogId,
    endLogId,
    viewAs,
    associateElevatedActivityLogs,
    isComplianceActivity,
    complianceActivityStaffId,
    lastSelectedScopeId,
    activityGroupsModel,
  ]);

  return (
    <S.Root>
      <S.Card>
        <S.IconWrap>
          <Search sx={{ fontSize: 64 }} />
        </S.IconWrap>
        <S.Title>Search activity</S.Title>
        <S.Lead>
          Set your criteria on the left, then press Search. You can also load the
          {' '}
          {latestLogsButtonLabel.toLowerCase()}
          .
        </S.Lead>
        <ActivityLogsSearchControls setModal={setModal} layout="card" widePane />
        <S.Summary>
          {summary.map((line, index) => (
            <S.SummaryRow key={line.label} $index={index}>
              <S.SummaryIcon aria-hidden>
                {SUMMARY_ICONS[line.icon]}
              </S.SummaryIcon>
              <S.SummaryText>
                <S.SummaryLabel>{line.label}</S.SummaryLabel>
                {line.searchTerms?.length || line.searchOptionBadges?.length ? (
                  <>
                    {line.searchTerms?.map((term, termIndex) => (
                      <S.SummarySearchTerm
                        key={`${line.label}_term_${termIndex}_${term}`}
                        title={term}
                      >
                        {term}
                      </S.SummarySearchTerm>
                    ))}
                    {line.searchOptionBadges?.length ? (
                      <S.SummaryBadges title={line.searchOptionBadges.join(', ')}>
                        {line.searchOptionBadges.map((badge) => (
                          <S.SummaryBadge key={`${line.label}_opt_${badge}`}>
                            {badge}
                          </S.SummaryBadge>
                        ))}
                      </S.SummaryBadges>
                    ) : null}
                  </>
                ) : (
                  <S.SummaryValue
                    $maxLines={line.maxLines}
                    title={line.value}
                    style={line.maxLines
                      ? { WebkitBoxOrient: 'vertical' }
                      : undefined}
                  >
                    {line.value}
                  </S.SummaryValue>
                )}
              </S.SummaryText>
            </S.SummaryRow>
          ))}
        </S.Summary>
        <S.Actions>
          {canExitToResults ? (
            <S.ExitToLogsBtn
              type="button"
              data-analytics="activityLogs_exit_to_logs"
              onClick={exitToResults}
            >
              <ReceiptLong sx={{ fontSize: 18 }} />
              Exit to logs
            </S.ExitToLogsBtn>
          ) : null}
          <S.PrimaryBtn
            type="button"
            data-analytics="activityLogs_search_submit"
            onClick={() => {
              logButtonClick('activityLogs_search_submit');
              onSearch();
            }}
          >
            Search
          </S.PrimaryBtn>
          <S.SecondaryBtn
            type="button"
            data-analytics="activityLogs_search_latest"
            onClick={() => {
              logButtonClick('activityLogs_search_latest');
              onLatest();
            }}
          >
            {latestLogsButtonLabel}
          </S.SecondaryBtn>
        </S.Actions>
      </S.Card>
    </S.Root>
  );
};

export default ActivitySearch;
